import React, { useCallback, useEffect, useState } from 'react';
import { AiOutlineWarning } from 'react-icons/ai';
import { useDispatch } from 'react-redux';

import {
  PaginatedTableProps,
  BaseEntity,
} from '~/components/Table/PaginatedTable/interface';
import { resetTaskAssignedError } from '~/store/modules/tasks/actions';
import { ICamundaUser } from '~/store/modules/users/types';
import colors from '~/styles/colors';

import Button from '../Button';
import Modal from '../Modal';
import PaginatedTable from '../Table/PaginatedTable';
import {
  Container,
  TableContainer,
  FooterContainer,
  FailureTitleContainer,
  FailureTitle,
} from './styles';

type ConfirmationModalProps<Entity extends BaseEntity> = Omit<
  PaginatedTableProps<Entity>,
  'entities' | 'emptyTable' | 'sorting' | 'menu'
> & {
  open: boolean;
  setOpenModal: (open: boolean) => void;
  title: string;
  selectedEntities: [Array<Entity>, (data: Array<Entity>) => void];
  failureList: Array<Entity>;
};

function ConfirmationModal<Entity extends BaseEntity>({
  open,
  setOpenModal,
  title,
  selectedEntities,
  failureList,
  ...tableProps
}: ConfirmationModalProps<Entity>) {
  const [selectedItems, setSelectedItems] = selectedEntities;

  const [confirmedItems, setConfirmedItems] = useState<Entity[]>(selectedItems);

  const dispatch = useDispatch();

  const isFailure = failureList.length > 0;

  useEffect(() => {
    setConfirmedItems(selectedItems);
  }, [selectedItems]);

  const isDifferent = useCallback(() => {
    if (
      selectedItems.every((selectedItem) =>
        confirmedItems.some(
          (confirmedItem) => selectedItem.id === confirmedItem.id,
        ),
      )
    ) {
      return true;
    }
    return false;
  }, [selectedItems, confirmedItems]);

  const handleResetErrors = useCallback(() => {
    dispatch(resetTaskAssignedError());
  }, [dispatch]);

  return (
    <Container>
      <Modal
        title={isFailure ? '' : `${title} selecionadas`}
        open={open}
        modalState={() => {
          setOpenModal(!open);
        }}
        disableScrollBar
      >
        {isFailure && (
          <FailureTitleContainer>
            <FailureTitle>
              <AiOutlineWarning size={32} color={colors.primary} />
              <h2>Atenção</h2>
            </FailureTitle>
            <span>As tarefas abaixo já têm usuário atribuído.</span>
          </FailureTitleContainer>
        )}
        <TableContainer>
          <PaginatedTable
            entities={isFailure ? failureList : selectedItems}
            selectedEntities={
              isFailure ? undefined : [confirmedItems, setConfirmedItems]
            }
            {...tableProps}
          />
        </TableContainer>

        <FooterContainer>
          {isFailure ? (
            <Button
              onClick={() => {
                setOpenModal(false);
                handleResetErrors();
                setSelectedItems([]);
              }}
            >
              Entendi
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setOpenModal(false);
                }}
              >
                Cancelar
              </Button>

              <Button
                disabled={isDifferent()}
                onClick={() => {
                  setOpenModal(false);
                  setSelectedItems(confirmedItems);
                }}
              >
                Confirmar alterações
              </Button>
            </>
          )}
        </FooterContainer>
      </Modal>
    </Container>
  );
}

export default ConfirmationModal;
